import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';

// Initialize Stripe
const stripePromise = loadStripe('pk_test_51S2Gb2E0xFsZ8XF1m7xdGwImR07M4mDOrWUcZYN2M5nb4YCkm5LOtYRzaC38v5qHz7djQcpWFE8qEZGv5BheR9tr00sbsUHf5q'); // Replace with your actual Stripe publishable key

const PaymentForm = ({ amount, clientSecret, onSuccess, onError, loading }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [postalError, setPostalError] = useState('');

  const formatCanadianPostalCode = (value) => {
    // Remove non-alphanumeric characters
    let cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

    // Limit to 6 characters (A1A1A1)
    cleaned = cleaned.slice(0, 6);

    // Insert a space after the third character: A1A 1A1
    if (cleaned.length > 3) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
    }

    return cleaned;
  };

  const isValidCanadianPostalCode = (value) => {
    const normalized = value.toUpperCase().trim();
    // Basic Canadian postal code pattern: A1A 1A1 (space optional)
    const regex = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/;
    return regex.test(normalized);
  };

  const handlePostalChange = (event) => {
    const formatted = formatCanadianPostalCode(event.target.value);
    setPostalCode(formatted);

    if (formatted && !isValidCanadianPostalCode(formatted)) {
      setPostalError('Enter a valid Canadian postal code (e.g. A1A 1A1).');
    } else {
      setPostalError('');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Final validation for postal code before submitting
    if (!isValidCanadianPostalCode(postalCode)) {
      setError('Please enter a valid Canadian postal code (e.g. A1A 1A1).');
      setPostalError('Enter a valid Canadian postal code (e.g. A1A 1A1).');
      return;
    }

    setProcessing(true);
    setError('');

    const cardElement = elements.getElement(CardElement);

    try {
      // Confirm the PaymentIntent with card details using the client secret
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            address: {
              country: 'CA', // or selectedCountry
              postal_code: postalCode.replace(/\s/g, '').toUpperCase(),
            },
          },
        }
      });

      if (error) {
        setError(error.message || 'Payment failed');
        onError(error.message || 'Payment failed');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess(paymentIntent);
      } else {
        onError('Payment not completed');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      onError('An unexpected error occurred');
    } finally {
      setProcessing(false);
    }
  };

  const cardElementOptions = {
    // We collect postal code separately to support Canadian format
    hidePostalCode: true,
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Payment Details</Typography>

        <TextField
          label="Postal Code"
          variant="outlined"
          fullWidth
          value={postalCode}
          onChange={handlePostalChange}
          error={Boolean(postalError)}
          helperText={postalError || 'Enter your Canadian postal code (e.g. A1A 1A1).'}
          inputProps={{ maxLength: 7 }}
        />

        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <CardElement options={cardElementOptions} />
        </Box>

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          Amount: ${amount}
        </Typography>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={!stripe || processing || loading}
          startIcon={processing ? <CircularProgress size={20} /> : null}
        >
          {processing ? 'Processing...' : `Pay $${amount}`}
        </Button>
      </Stack>
    </Box>
  );
};

const StripePaymentForm = ({ amount, clientSecret, onSuccess, onError, loading }) => {
  return (
    <Elements
    stripe={stripePromise}
    options={{
      clientSecret: clientSecret,
      appearance: { theme: "stripe" }, // optional
      defaultValues: {
        billingDetails: {
          address: {
            country: "CA",
          },
        },
      },
    }}
  >
      <PaymentForm 
        amount={amount} 
        clientSecret={clientSecret}
        onSuccess={onSuccess} 
        onError={onError} 
        loading={loading}
      />
    </Elements>
  );
};

export default StripePaymentForm;
